import type { CalcResult, SpeciesId } from '../types.ts';
import { Sprite } from './Sprite.tsx';

// Damage calc result rendered as banner + 16-roll damage band + KO chip.
// Four variants drive visual emphasis: super-effective gets a red rail,
// resisted gets a muted border, immune is struck through with an "immune"
// tag, standard is neutral.
export function DamageCalcCard({ calc, compact = false }: { calc: CalcResult; compact?: boolean }) {
  // Defensive: bail rather than crashing the whole conversation tree if the
  // tool result omits any of the fields the card depends on. This shouldn't
  // happen with the current handler but a future tool change shouldn't be
  // able to crash the renderer.
  if (!calc || !calc.attacker?.species || !calc.defender?.species || !calc.damage) {
    return (
      <div className="mono" style={{ color: 'var(--fg-4)', fontSize: 11, opacity: 0.6 }}>
        damage calc · missing fields
      </div>
    );
  }
  const variantStyle = variantStyles(calc.variant ?? 'standard');
  const koPctOf100 = Math.min(100, calc.damage.maxPct);
  const koTextColor = calc.isOhko
    ? 'var(--neg)'
    : calc.damage.maxPct >= 50
      ? 'var(--warn)'
      : 'var(--fg-2)';

  return (
    <div
      className="vb-card"
      style={{
        borderColor: variantStyle.borderColor,
        borderLeftWidth: 2,
        borderLeftColor: variantStyle.accent,
      }}
    >
      <div className="vb-card-header" style={{ borderBottomColor: variantStyle.borderColor }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <Sprite species={calc.attacker.species as SpeciesId} size={28} />
          <span
            className="mono"
            style={{
              fontSize: 11,
              color: 'var(--fg-1)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {calc.move}
          </span>
          <span style={{ color: 'var(--fg-4)' }}>→</span>
          <Sprite species={calc.defender.species as SpeciesId} size={28} />
        </div>
        <span
          className="caps mono"
          style={{
            fontSize: 9,
            color: variantStyle.accent,
            letterSpacing: '0.08em',
          }}
          title={
            calc.effectiveness
              ? `${calc.effectiveness.moveType} vs ${calc.effectiveness.defenderTypes.join('/')}`
              : undefined
          }
        >
          {calc.effectiveness ? effectivenessChipLabel(calc.effectiveness) : calc.variant}
        </span>
      </div>

      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <span
            className="mono tabular"
            style={{ fontSize: 16, fontWeight: 500, color: 'var(--fg-1)' }}
          >
            {calc.variant === 'immune' ? (
              <span style={{ textDecoration: 'line-through', color: 'var(--fg-4)' }}>
                {calc.damage.minPct.toFixed(1)} – {calc.damage.maxPct.toFixed(1)}%
              </span>
            ) : (
              <>
                {calc.damage.minPct.toFixed(1)} – {calc.damage.maxPct.toFixed(1)}%
              </>
            )}
          </span>
          <span
            className="mono caps"
            style={{
              fontSize: 10,
              color: koTextColor,
              padding: '2px 7px',
              border: `1px solid ${koTextColor === 'var(--neg)' ? 'var(--neg)' : 'var(--line-2)'}`,
              borderRadius: 2,
              letterSpacing: '0.08em',
            }}
          >
            {calc.koChance.text}
          </span>
        </div>

        {!compact && calc.variant !== 'immune' && (
          <div className="damage-band">
            <div
              className="fill"
              style={{
                left: `${calc.damage.minPct}%`,
                width: `${Math.max(0, calc.damage.maxPct - calc.damage.minPct)}%`,
              }}
            />
            <div className="ko-marker" style={{ left: '100%' }} />
            {/* visible roll range marker overlay */}
            <div
              className="ko-marker"
              style={{ left: `${koPctOf100}%`, background: 'var(--neg)', opacity: 0.8 }}
            />
          </div>
        )}

        <div
          className="mono"
          style={{
            color: 'var(--fg-3)',
            fontSize: 10.5,
            lineHeight: 1.5,
            wordBreak: 'break-word',
          }}
        >
          {calc.desc}
        </div>
      </div>
    </div>
  );
}

// "×2 super" / "×0.5 resist" / "neutral" / "immune" — same data the variant
// already implies, but with the explicit multiplier so the user can sanity-
// check the agent's narration. The multiplier comes straight from the
// type-chart lookup in calc-damage.ts; it cannot disagree with the math.
function effectivenessChipLabel(eff: NonNullable<CalcResult['effectiveness']>): string {
  if (eff.label === 'immune') return 'immune';
  if (eff.label === 'neutral') return 'neutral';
  const mult = eff.multiplier;
  const prefix = `×${mult % 1 === 0 ? mult : mult.toFixed(2).replace(/\.?0+$/, '')}`;
  return eff.label === 'super_effective' ? `${prefix} super` : `${prefix} resist`;
}

function variantStyles(v: CalcResult['variant']): {
  borderColor: string;
  accent: string;
} {
  switch (v) {
    case 'super':
      return { borderColor: 'rgba(201,119,102,0.40)', accent: 'var(--neg)' };
    case 'resisted':
      return { borderColor: 'var(--line-1)', accent: 'var(--fg-4)' };
    case 'immune':
      return { borderColor: 'var(--line-1)', accent: 'var(--fg-4)' };
    default:
      return { borderColor: 'var(--line-1)', accent: 'var(--accent-1)' };
  }
}
